import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useGoldPrice } from '@/hooks/useGoldPrice';

export default function SpotCard() {
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  const { getPrice, getChange, formatPrice, getDirection } = useGoldPrice();

  const goldPrice = getPrice('黄金');
  const change = getChange('黄金');
  const sign = change >= 0 ? '+' : '';
  const isUp = change >= 0;

  const [priceFlash, setPriceFlash] = useState<'up' | 'down' | null>(null);
  const prevPriceRef = useRef(goldPrice);

  useEffect(() => {
    if (goldPrice > 0 && prevPriceRef.current > 0 && goldPrice !== prevPriceRef.current) {
      const dir = getDirection('黄金');
      if (dir !== 'flat') {
        setPriceFlash(dir);
        const timer = setTimeout(() => setPriceFlash(null), 800);
        return () => clearTimeout(timer);
      }
    }
    prevPriceRef.current = goldPrice;
  }, [goldPrice, getDirection]);

  const flashClass =
    priceFlash === 'up'
      ? 'animate-flash-up'
      : priceFlash === 'down'
        ? 'animate-flash-down'
        : '';

  return (
    <div className="mx-3 -mt-5 relative z-10 bg-white rounded-2xl p-5 border border-gold-100/50">
      {/* 顶部标签 */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-gold-500 text-xs tracking-wider font-semibold">
          {t('physicalGold')}
        </span>
        <span className="text-gold-300 text-xs">·</span>
        <span className="text-brown-500 text-xs tracking-wider">
          {t('spotReserve')}
        </span>
      </div>

      <div className="flex items-start justify-between gap-4">
        {/* 左侧：标题+描述+标签+按钮 */}
        <div className="flex-1 min-w-0">
          <h2 className="text-brown-900 text-xl font-bold leading-tight mb-1">
            实物黄金现货预约
          </h2>
          <p className="text-brown-400 text-xs mt-1 mb-3 leading-relaxed">
            {t('heroDesc')}
          </p>

          {/* 标签 */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="bg-brown-50 text-brown-600 text-xs px-3 py-1.5 rounded-md border border-brown-100">
              {t('productLabel')}
            </span>
            <span className="bg-brown-50 text-brown-600 text-xs px-3 py-1.5 rounded-md border border-brown-100">
              {t('productBrand')}
            </span>
          </div>

          {/* 按钮区域 */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/lock-price')}
              className="inline-flex items-center gap-1.5 bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-500 hover:to-gold-600 text-brown-950 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-lock-line text-sm"></i>
              </span>
              {t('heroCta')}
            </button>
            <button
              onClick={() => navigate('/deposit')}
              className="inline-flex items-center gap-1.5 bg-white border-2 border-amber-400 text-amber-600 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap active:scale-95 hover:bg-amber-50"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-line text-sm"></i>
              </span>
              入金
            </button>
          </div>
        </div>

        {/* 右侧：实时金价 */}
        <div className="flex-shrink-0 text-right">
          <p className="text-brown-400 text-xs mb-1">
            {t('liveGoldPrice')}
          </p>
          <div className="flex items-baseline justify-end gap-0.5">
            <span className="text-brown-500 text-sm font-medium">¥</span>
            <span className={`text-brown-900 text-[32px] font-bold tracking-tight ${flashClass}`}>
              {goldPrice > 0 ? formatPrice('黄金', 2) : '---'}
            </span>
            <span className="text-brown-400 text-xs ml-1">/克</span>
          </div>

          {/* 实时更新小红点 */}
          <div className="flex items-center justify-end gap-1.5 mt-2">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
            <span className="text-brown-400 text-[10px]">{t('liveUpdate')}</span>
          </div>

          {/* 今日涨幅 */}
          <div className="flex items-center justify-end gap-1 mt-1.5">
            <span className="text-brown-400 text-xs">{t('todayChange')}</span>
            <span
              className={`text-xs font-semibold ${isUp ? 'text-red-500' : 'text-green-500'}`}
            >
              {goldPrice > 0 ? `${sign}${change.toFixed(2)}%` : '+0.00%'}
            </span>
          </div>

          {/* 箭头 */}
          <button
            onClick={() => navigate('/market')}
            className="mt-3 w-8 h-8 rounded-full bg-gold-50 flex items-center justify-center ml-auto cursor-pointer hover:bg-gold-100 transition-colors"
          >
            <span className="w-4 h-4 flex items-center justify-center text-gold-500">
              <i className="ri-arrow-right-s-line text-base"></i>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}